/**
 * Data-retention admin hooks.
 *
 * Backs the admin Data Retention screen (`src/app/(admin)/data-retention.tsx`).
 * `retention_config` holds one row per purge category (all seeded DISABLED —
 * enabling one is a deliberate destructive decision); `retention_run_log`
 * records every run with per-category results. Both tables are admin-RLS'd
 * (config: SELECT/UPDATE, run log: SELECT) — there is deliberately no insert
 * path from the client: rows are seeded by migration, runs are written by the
 * SECURITY DEFINER runner.
 *
 * Modeled on `src/lib/feature-flags/index.ts`.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured, useAuth } from '@/lib/supabase';
import { logAdminAction } from '@/lib/admin/log-admin-action';
import { reportError } from '@/lib/errors';
import type { Database } from '@/lib/supabase/types';

export { parseRunResults, type RetentionRunResult, type RetentionResultStatus } from './results';

export type RetentionConfig = Database['public']['Tables']['retention_config']['Row'];
export type RetentionRun = Database['public']['Tables']['retention_run_log']['Row'];
export type RetentionRunStatus = 'running' | 'success' | 'partial' | 'failed';

export const retentionKeys = {
  config: () => ['retention', 'config'] as const,
  runs: (limit: number) => ['retention', 'runs', limit] as const,
};

/** All retention categories, for the admin management screen. */
export function useRetentionConfig() {
  return useQuery({
    queryKey: retentionKeys.config(),
    queryFn: async (): Promise<RetentionConfig[]> => {
      const { data, error } = await supabase
        .from('retention_config')
        .select('*')
        .order('key', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: isSupabaseConfigured(),
    staleTime: 30 * 1000,
  });
}

/**
 * Update a category's `enabled` / `retention_days` (admin).
 * UPDATE only — RLS grants no INSERT, categories are seeded by migration.
 */
export function useUpdateRetentionConfig() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      key: string;
      enabled: boolean;
      retention_days: number;
    }): Promise<RetentionConfig> => {
      const { data, error } = await supabase
        .from('retention_config')
        .update({
          enabled: input.enabled,
          retention_days: input.retention_days,
          updated_at: new Date().toISOString(),
          updated_by: user?.id ?? null,
        })
        .eq('key', input.key)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (saved) => {
      // Retention governs the DELETION of user data — a change here is exactly
      // what an audit trail exists for.
      void logAdminAction({
        action: 'retention_config_updated',
        targetType: 'retention_config',
        targetId: saved.key,
        details: { key: saved.key, enabled: saved.enabled, retention_days: saved.retention_days },
      });
      queryClient.invalidateQueries({ queryKey: ['retention'] });
    },
    onError: (err) =>
      reportError(err, { context: 'useUpdateRetentionConfig', level: 'warning', silent: true }),
  });
}

/** Recent retention runs, newest first. */
export function useRetentionRuns(limit = 20) {
  return useQuery({
    queryKey: retentionKeys.runs(limit),
    queryFn: async (): Promise<RetentionRun[]> => {
      const { data, error } = await supabase
        .from('retention_run_log')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
    enabled: isSupabaseConfigured(),
    staleTime: 30 * 1000,
  });
}

/** Trigger an immediate retention run via the admin-gated edge function. */
export function useRunRetentionNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ runId: string }> => {
      const { data, error } = await supabase.functions.invoke('retention-run', { body: {} });
      if (error) throw error;
      return data as { runId: string };
    },
    onSuccess: (result) => {
      // Running retention on demand irreversibly deletes data. If any single
      // admin action must be attributable afterwards, it is this one.
      void logAdminAction({
        action: 'retention_run_started',
        targetType: 'retention_run',
        targetId: result.runId,
      });
      queryClient.invalidateQueries({ queryKey: ['retention'] });
    },
    onError: (err) =>
      reportError(err, { context: 'useRunRetentionNow', level: 'warning', silent: true }),
  });
}
